import { RootStore } from './root';
import { WebAuthnChallengeData } from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { PublicKeyCredentialWithJSON } from '../types';
import {
  DOM_EXCEPTION_NAMES,
  ERROR_CODES,
  extractErrorKeyByErrorCode,
} from '../utils/errorHandler';
import { passkeyConnectionsSucceedSrc5Evid5022 } from '@wix/bi-logger-identity-data-account-settings/v2';
import { logInErrorModalViewSrc6Evid11 } from '@wix/bi-logger-identity-data/v2';

export class WebAuthnStore {
  private readonly rootStore: RootStore;
  private webAuthnAbortController: AbortController | null = null;
  private _passkeyFlowDisabled: boolean = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  get isPasskeyFlowDisabled(): boolean {
    return this._passkeyFlowDisabled;
  }

  public disablePasskeyFlow(): void {
    this._passkeyFlowDisabled = true;
  }

  private handleWebAuthnError(error: unknown): void {
    // return: regular flow move to next step, usernameless flow silent fallback
    // throw: regular flow show error toast, usernameless flow show error toast
    if (!(error instanceof Error)) {
      return;
    }

    if (error.message === ERROR_CODES.WEBAUTHN_FAILED_OR_TIMEOUT) {
      this.disablePasskeyFlow();
      this.reportPasskeyError(ERROR_CODES.WEBAUTHN_FAILED_OR_TIMEOUT);
      this.rootStore.notificationStore.openErrorToast(
        this.rootStore.i18n.t(
          extractErrorKeyByErrorCode(ERROR_CODES.WEBAUTHN_FAILED_OR_TIMEOUT),
        ),
      );
      throw new Error(ERROR_CODES.WEBAUTHN_FAILED_OR_TIMEOUT);
    }

    // Challenge failed: silent fallback
    if (error.message === ERROR_CODES.WEBAUTHN_CHALLENGE_FAILED) {
      return;
    }

    // NotAllowedError: user cancelled (error raise only in regular flow)
    if (error.name === DOM_EXCEPTION_NAMES.NOT_ALLOWED_ERROR) {
      return;
    }

    // AbortError: abort usernameless flow to use regular flow (intentional error)
    if (error.name === DOM_EXCEPTION_NAMES.ABORT_ERROR) {
      return;
    }

    // Other errors
    return;
  }

  private async validateWebAuthnSupport(): Promise<void> {
    if (!window.PublicKeyCredential) {
      throw new Error(ERROR_CODES.WEBAUTHN_NOT_SUPPORTED);
    }
    const isSupported =
      await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    if (!isSupported) {
      throw new Error(ERROR_CODES.WEBAUTHN_NOT_SUPPORTED);
    }
  }

  private async createWebAuthnChallenge(
    email?: string,
  ): Promise<WebAuthnChallengeData> {
    try {
      const challengeResponse =
        await this.rootStore.apiStore.getWebauthnChallenge({
          loginId: { email },
        });
      if (!challengeResponse.webauthn) {
        throw new Error(ERROR_CODES.WEBAUTHN_CHALLENGE_FAILED);
      }
      return challengeResponse.webauthn;
    } catch (error) {
      throw new Error(ERROR_CODES.WEBAUTHN_CHALLENGE_FAILED);
    }
  }

  private parseCredentialRequestOptions(
    challengeData: WebAuthnChallengeData,
  ): PublicKeyCredentialRequestOptions {
    const { publicKey } = JSON.parse(
      challengeData.publicKeyCredentialRequestOptionsJson!,
    );

    return (
      PublicKeyCredential as unknown as PublicKeyCredentialWithJSON
    ).parseRequestOptionsFromJSON(publicKey);
  }

  private async requestCredentialFromBrowser(
    credentialRequestOptions: PublicKeyCredentialRequestOptions,
    useConditionalUI?: boolean,
  ): Promise<PublicKeyCredential> {
    const credential = await navigator.credentials.get({
      publicKey: credentialRequestOptions,
      ...(useConditionalUI && {
        mediation: 'conditional' as CredentialMediationRequirement,
        signal: this.webAuthnAbortController?.signal,
      }),
    });

    if (!credential) {
      throw new Error(ERROR_CODES.WEBAUTHN_FAILED_OR_TIMEOUT);
    }

    return credential as PublicKeyCredential;
  }

  private async isConditionalMediationAvailable(): Promise<boolean> {
    return PublicKeyCredential.isConditionalMediationAvailable();
  }

  private reportPasskeySuccess(): void {
    this.rootStore.biLogger.report(
      passkeyConnectionsSucceedSrc5Evid5022({
        origin: 'login',
        ldSessionID: this.rootStore.sessionId.get(),
      }),
    );
  }

  public reportPasskeyError(errorCode: string): void {
    this.rootStore.biLogger.report(
      logInErrorModalViewSrc6Evid11({
        origin: 'login',
        error_name: errorCode,
        method: 'passkey',
      }),
    );
  }

  public async authenticateWithWebAuthn(email: string): Promise<{
    credential: PublicKeyCredential;
    challenge: WebAuthnChallengeData;
  } | null> {
    try {
      await this.validateWebAuthnSupport();

      const challenge = await this.createWebAuthnChallenge(email);

      const credentialRequestOptions =
        this.parseCredentialRequestOptions(challenge);
      const credential = await this.requestCredentialFromBrowser(
        credentialRequestOptions,
        false,
      );

      this.reportPasskeySuccess();
      return { credential, challenge };
    } catch (error) {
      this.handleWebAuthnError(error);
      return null;
    }
  }

  public async authenticateWithUsernamelessWebAuthn(): Promise<{
    credential: PublicKeyCredential;
    challenge: WebAuthnChallengeData;
  } | null> {
    try {
      await this.validateWebAuthnSupport();

      const isConditionalMediationAvailable =
        await this.isConditionalMediationAvailable();
      if (!isConditionalMediationAvailable) {
        return null;
      }

      const challenge = await this.createWebAuthnChallenge();

      const credentialRequestOptions =
        this.parseCredentialRequestOptions(challenge);

      this.webAuthnAbortController = new AbortController();

      const credential = await this.requestCredentialFromBrowser(
        credentialRequestOptions,
        true,
      );

      this.reportPasskeySuccess();
      return { credential, challenge };
    } catch (error) {
      this.handleWebAuthnError(error);
      return null;
    }
  }

  public abortWebAuthnAuthentication(): void {
    if (this.webAuthnAbortController) {
      this.webAuthnAbortController.abort();
      this.webAuthnAbortController = null;
    }
  }
}
